import { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Sparkles,
  User,
  Music as MusicIcon,
  Activity,
  Plus,
  Check,
  Loader2,
  RefreshCcw,
  AlertCircle,
  X,
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import CratePickerModal from '@/components/CratePickerModal';

const MODE_DEFS = [
  {
    id: 'artist',
    label: 'Artist',
    icon: User,
    hint: 'Same artist + collaborators',
  },
  {
    id: 'vibe',
    label: 'Vibe',
    icon: MusicIcon,
    hint: 'Similar genre & mood',
  },
  {
    id: 'bpm_key',
    label: 'BPM / Key',
    icon: Activity,
    hint: 'Mix-compatible tempo & key',
  },
];

const MATCH_MODE_LABEL = {
  artist: 'Artist match',
  vibe: 'Vibe match',
  bpm_key: 'Mix-compatible',
  blend: 'Multi-match',
};

/**
 * AI Similar Tracks discovery modal.
 * Seeded from a card. User toggles match modes (artist/vibe/bpm_key) — any combo.
 * Results show as discovery cards with "Add to collection" affordance.
 */
export default function DiscoverSimilarModal({ seedCard, open, onClose, onAdded }) {
  const [modes, setModes] = useState(['vibe']);
  const [loading, setLoading] = useState(false);
  const [tracks, setTracks] = useState([]);
  const [cached, setCached] = useState(false);
  const [error, setError] = useState(null);
  // Per-track add status: { [`${artist}::${title}`]: 'idle'|'adding'|'added'|'duplicate' }
  const [addStatus, setAddStatus] = useState({});
  // Crate-picker state — opens when user clicks "Add" on any discovered track.
  // We stash the pending track so doAdd can fire once a bucket is chosen.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pendingTrack, setPendingTrack] = useState(null);

  const toggleMode = (modeId) => {
    setModes((prev) => {
      if (prev.includes(modeId)) {
        // Don't allow zero modes — keep at least one selected
        if (prev.length === 1) return prev;
        return prev.filter((m) => m !== modeId);
      }
      return [...prev, modeId];
    });
  };

  const fetchSimilar = useCallback(
    async ({ refresh = false } = {}) => {
      if (!seedCard?.id) return;
      setLoading(true);
      setError(null);
      try {
        const { data } = await api.post('/discover/similar', {
          card_id: seedCard.id,
          modes,
          force_refresh: refresh,
        });
        const tracksList = data.tracks || [];
        setTracks(tracksList);
        setCached(!!data.cached);
        if (!tracksList.length) {
          setError('No similar tracks found for this combination — try a different mode.');
        } else {
          // Batch-check which of these tracks are already in the user's collection.
          // Runs in background — non-blocking; modal renders results immediately.
          try {
            const ownedResp = await api.post('/cards/owned-check', {
              tracks: tracksList.map((t) => ({ artist: t.artist, title: t.title })),
            });
            const ownedMap = ownedResp.data?.owned || {};
            if (Object.keys(ownedMap).length) {
              setAddStatus((prev) => {
                const next = { ...prev };
                for (const [key, status] of Object.entries(ownedMap)) {
                  // 'active' = already in collection; 'owned' = already marked owned
                  next[key] = status === 'owned' ? 'in-collection-owned' : 'in-collection';
                }
                return next;
              });
            }
          } catch (e) {
            // non-critical — modal still works, user will just see Add buttons
            console.warn('owned-check failed:', e);
          }
        }
      } catch (e) {
        const msg = e?.response?.data?.detail || 'Failed to load similar tracks';
        setError(msg);
        toast.error(msg);
      } finally {
        setLoading(false);
      }
    },
    [seedCard?.id, modes]
  );

  // Auto-fetch when modal opens OR modes change
  useEffect(() => {
    if (!open) {
      // Reset state on close
      setTracks([]);
      setAddStatus({});
      setError(null);
      return;
    }
    fetchSimilar();
  }, [open, fetchSimilar]);

  const handleAdd = (track) => {
    // Open the crate picker. The actual API call fires from doAdd
    // once the user chooses a bucket. We keep the discovered track in
    // pendingTrack state so doAdd has access to artist/title/reason.
    setPendingTrack(track);
    setPickerOpen(true);
  };

  const doAdd = async (chosenBucket) => {
    const track = pendingTrack;
    if (!track) return;
    setPickerOpen(false);
    const key = `${track.artist}::${track.title}`;
    setAddStatus((s) => ({ ...s, [key]: 'adding' }));
    try {
      const { data } = await api.post('/cards/from-discovery', {
        artist: track.artist,
        title: track.title,
        reason: track.reason,
        target_bucket: chosenBucket,
      });
      setAddStatus((s) => ({ ...s, [key]: data.duplicate ? 'duplicate' : 'added' }));
      if (data.duplicate) {
        toast.info(`Already in your collection: ${track.title}`);
      } else {
        toast.success(`Added to ${data.card?.genre_bucket || 'your'} crate: ${track.title}`);
        onAdded?.(data.card);
      }
    } catch (e) {
      setAddStatus((s) => ({ ...s, [key]: 'idle' }));
      toast.error(e?.response?.data?.detail || `Failed to add ${track.title}`);
    } finally {
      setPendingTrack(null);
    }
  };

  const seedLine = seedCard
    ? `${seedCard.title} · ${seedCard.artist}${
        seedCard.bpm_key?.tempo
          ? ` · ${Math.round(seedCard.bpm_key.tempo)} BPM${
              seedCard.bpm_key.key_name
                ? ` · ${seedCard.bpm_key.key_name}${
                    seedCard.bpm_key.scale === 'minor' ? 'm' : ''
                  }`
                : ''
            }`
          : ''
      }`
    : '';

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        /* Same pattern as MusicCardDetail — DialogContent is invisible;
           the visible modal is the .crate-neon-border sandwich below so
           the animated gradient ring can wrap the entire dialog.
           `!rounded-2xl` overrides Shadcn's default `sm:rounded-lg` (8px)
           so the DialogContent's own rounded box matches our wrapper's
           16px radius exactly — prevents sub-pixel drift on the corners.
           `[&>button]:hidden` hides Radix's default close X (rendered as a
           direct-child button of DialogContent) — it was painting a focus
           rectangle over the neon border's top-right corner. Our own close
           button lives safely inside the header instead. */
        className="!bg-transparent !border-0 !p-0 !shadow-none !rounded-2xl max-w-md sm:max-w-2xl w-[95vw] max-h-[92vh] overflow-visible !grid-cols-1 !gap-0 [&>button]:hidden"
        data-testid="discover-similar-modal"
      >
        {/* Pattern A: outer = animated gradient bg, inner = solid card on top.
            The 2px padding on .crate-neon-border is the visible "border" ring.
            `overflow-hidden` ensures no sub-pixel bleed at the rounded corners. */}
        <div className="crate-neon-border rounded-2xl shadow-2xl overflow-hidden">
          {/* Inner card: `pr-1` reserves a small gutter so the scrollbar
              doesn't sit flush against the neon ring on the right edge.
              `scrollbar-thin` + custom thumb style keeps it discreet. */}
          <div className="bg-card rounded-[14px] max-h-[calc(92vh-4px)] overflow-y-auto discover-modal-scroll relative">
            {/* Custom close button — sits inside the header padding so it
                never overlaps the animated border corner. */}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              data-testid="discover-similar-close"
              className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full grid place-items-center text-muted-foreground hover:text-foreground hover:bg-secondary/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] transition-colors duration-150"
            >
              <X className="w-4 h-4" />
            </button>
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-border">
          <DialogTitle className="font-display flex items-center gap-2 text-lg">
            <Sparkles className="w-5 h-5 text-[hsl(var(--primary))]" />
            Discover similar tracks
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground truncate">
            Seeded from: <span className="text-foreground/80">{seedLine}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="px-5 py-4 space-y-4">
          {/* Mode pill toggles */}
          <div>
            <div className="text-[10px] uppercase font-mono text-muted-foreground mb-2">
              Match by · combine any
            </div>
            <div className="flex flex-wrap gap-2" role="group" aria-label="Match modes">
              {MODE_DEFS.map((m) => {
                const active = modes.includes(m.id);
                const Icon = m.icon;
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => toggleMode(m.id)}
                    data-testid={`discover-mode-${m.id}`}
                    aria-pressed={active}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] ${
                      active
                        ? 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-[hsl(var(--primary))]'
                        : 'bg-secondary text-muted-foreground border-border hover:bg-secondary/70 hover:text-foreground'
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {m.label}
                  </button>
                );
              })}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => fetchSimilar({ refresh: true })}
                disabled={loading}
                className="ml-auto rounded-full text-xs h-auto py-1.5 px-3"
                data-testid="discover-refresh-button"
                aria-label="Refresh suggestions"
              >
                <RefreshCcw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
            <div className="text-[10px] text-muted-foreground/70 mt-1.5">
              {MODE_DEFS.find((m) => m.id === modes[0])?.hint}
              {modes.length > 1 && ` + ${modes.length - 1} more`}
              {cached && !loading && <span className="ml-2 font-mono">· cached</span>}
            </div>
          </div>

          {/* Results */}
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={`discover-skeleton-${i}`} className="h-16 rounded-xl" />
              ))}
            </div>
          ) : error && tracks.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-background p-6 text-center">
              <AlertCircle className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
              <div className="text-sm text-muted-foreground">{error}</div>
            </div>
          ) : (
            <ul className="space-y-2" data-testid="discover-results">
              {tracks.map((t) => {
                const key = `${t.artist}::${t.title}`;
                const status = addStatus[key] || 'idle';
                return (
                  <li
                    key={key}
                    className="flex items-start gap-3 p-3 rounded-xl border border-border bg-background hover:border-[hsl(var(--primary))]/40 transition-colors duration-150"
                    data-testid="discover-result-card"
                  >
                    <div className="w-12 h-12 rounded-lg bg-secondary grid place-items-center shrink-0 overflow-hidden">
                      {t.artwork_url ? (
                        <img
                          src={t.artwork_url}
                          alt=""
                          loading="lazy"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            // Fall back to the GroovLens logo icon if the
                            // artwork 404s or the CDN blocks the request —
                            // hide the image and reveal the sibling logo
                            // placeholder underneath.
                            e.currentTarget.style.display = 'none';
                            const fb = e.currentTarget.nextElementSibling;
                            if (fb) fb.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      {/* GroovLens logo fallback — shown when there's no
                          artwork_url from iTunes OR the artwork image errors.
                          Sits at slightly reduced size + opacity so it reads
                          as a branded placeholder, not a broken image. */}
                      <div
                        className="w-full h-full items-center justify-center bg-secondary"
                        style={{ display: t.artwork_url ? 'none' : 'flex' }}
                        aria-hidden="true"
                      >
                        <img
                          src="/brand/groovlens-icon-transparent-256.png"
                          alt=""
                          className="w-7 h-7 opacity-70"
                          loading="lazy"
                        />
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <Badge
                          variant="outline"
                          className="text-[9px] uppercase font-mono px-1.5 py-0"
                        >
                          {MATCH_MODE_LABEL[t.match_mode] || 'Match'}
                        </Badge>
                      </div>
                      <div className="font-display font-semibold text-sm truncate">
                        {t.title}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">{t.artist}</div>
                      {t.reason && (
                        <div className="text-[11px] text-muted-foreground/70 mt-1 italic line-clamp-2">
                          {t.reason}
                        </div>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant={
                        status === 'added' || status === 'duplicate' ||
                        status === 'in-collection' || status === 'in-collection-owned'
                          ? 'secondary'
                          : 'default'
                      }
                      onClick={() => handleAdd(t)}
                      disabled={
                        status === 'adding' || status === 'added' || status === 'duplicate' ||
                        status === 'in-collection' || status === 'in-collection-owned'
                      }
                      className="rounded-lg shrink-0"
                      data-testid={`discover-add-${t.title.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      {status === 'adding' && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                      {(status === 'added' || status === 'duplicate' ||
                        status === 'in-collection' || status === 'in-collection-owned') && (
                        <Check className="w-3.5 h-3.5 mr-1.5" />
                      )}
                      {status === 'idle' && <Plus className="w-3.5 h-3.5 mr-1.5" />}
                      {status === 'added' && 'Added'}
                      {status === 'duplicate' && 'Owned'}
                      {status === 'in-collection' && 'In crate'}
                      {status === 'in-collection-owned' && 'Owned'}
                      {status === 'adding' && 'Adding…'}
                      {status === 'idle' && 'Add'}
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
          {/* /.bg-card inner — closes the solid card surface */}
          </div>
        {/* /.crate-neon-border outer — closes the animated gradient wrapper */}
        </div>
      </DialogContent>
      <CratePickerModal
        open={pickerOpen}
        onClose={() => {
          setPickerOpen(false);
          setPendingTrack(null);
        }}
        track={pendingTrack}
        suggestedBucket={seedCard?.genre_bucket}
        onConfirm={doAdd}
      />
    </Dialog>
  );
}
